import { z } from 'zod'

import { aiClient, apiClient } from '../client'
import { canopyPath } from '../routes'

const SUBSCORE_KEYS = [
  'defensibilityCompetitive',
  'defensibilityWorkflow',
  'defensibilityErrorTolerance',
  'defensibilityRevenueModel',
  'defensibilityDataMoat',
  'readinessProduct',
  'readinessLeadership',
  'readinessData',
  'readinessOps',
] as const

export const SUBSCORE_DISPLAY_ORDER: readonly SubscoreKey[] = SUBSCORE_KEYS
export type SubscoreKey = (typeof SUBSCORE_KEYS)[number]

export const confidenceSchema = z.enum(['high', 'medium', 'low'])
export type Confidence = z.infer<typeof confidenceSchema>

export const researchStatusSchema = z.enum(['queued', 'running', 'complete', 'failed'])
export type ResearchStatus = z.infer<typeof researchStatusSchema>

export const evidenceSchema = z.looseObject({
  text: z.string(),
  url: z.string().nullish(),
  sourceRef: z.string().nullish(),

  sourceType: z.enum(['web_search', 'uploaded_doc', 'manual']).nullish(),

  sourceDocumentId: z.string().nullish(),
  sourcePageRange: z.string().nullish(),
})

export type Evidence = z.infer<typeof evidenceSchema>

export const suggestedScoreSchema = z.looseObject({
  suggested: z.number().int().min(1).max(5),
  confidence: confidenceSchema,
  evidence: z.array(evidenceSchema).default([]),
})

export type SuggestedScore = z.infer<typeof suggestedScoreSchema>

export const suggestedScoresSchema = z.object(
  Object.fromEntries(SUBSCORE_KEYS.map((key) => [key, suggestedScoreSchema])) as Record<
    SubscoreKey,
    typeof suggestedScoreSchema
  >,
)

export type SuggestedScores = z.infer<typeof suggestedScoresSchema>

export const recommendationSchema = z.looseObject({
  action: z.string(),
  priority: z.enum(['critical', 'high', 'moderate', 'low']),
  owner: z.string().nullish(),
  deadline: z.string().nullish(),
})

export type Recommendation = z.infer<typeof recommendationSchema>

export const researchJobSchema = z.looseObject({
  job_id: z.string(),
  company_id: z.string(),
  status: researchStatusSchema,
  triggered_by: z.enum(['upload', 'manual', 'schedule']).nullish(),

  error_message: z.string().nullish(),

  suggested_scores: suggestedScoresSchema.nullish(),
  evidence_by_subscore: z.record(z.string(), z.array(evidenceSchema)).nullish(),
  recommendations: z.array(recommendationSchema).nullish(),

  delta_flags: z.record(z.string(), z.unknown()).nullish(),

  input_tokens: z.number().int().nullish(),
  output_tokens: z.number().int().nullish(),
  web_search_count: z.number().int().nullish(),
  cost_usd: z.number().nullish(),
})

export type ResearchJob = z.infer<typeof researchJobSchema>

export const kbSyncStartedSchema = z.looseObject({
  sync_id: z.string(),

  ingestion_job_id: z.string().nullish(),
  files: z.number().int().nullish(),
  message: z.string().nullish(),
})

export type KbSyncStarted = z.infer<typeof kbSyncStartedSchema>

export const researchStartedSchema = z.looseObject({
  job_id: z.string(),
  status: researchStatusSchema,

  created: z.boolean(),
})

export type ResearchStarted = z.infer<typeof researchStartedSchema>

export interface KbSyncFile {
  id: string

  file_name: string

  s3: string
}

export interface KbSyncInput {
  companyId: string
  companyName: string
  files: readonly KbSyncFile[]
  signal?: AbortSignal | undefined
}

export function startKbSync({
  companyId,
  companyName,
  files,
  signal,
}: KbSyncInput): Promise<KbSyncStarted> {
  return aiClient().post(canopyPath('/ai/kbsync'), {
    schema: kbSyncStartedSchema,
    body: { company_id: companyId, company_name: companyName, files },
    ...(signal ? { signal } : {}),
  })
}

export function startResearch(companyId: string, signal?: AbortSignal): Promise<ResearchStarted> {
  return apiClient().post(canopyPath('/ai/research'), {
    schema: researchStartedSchema,
    body: { company_id: companyId },
    ...(signal ? { signal } : {}),
  })
}

export function getResearchJob(jobId: string, signal?: AbortSignal): Promise<ResearchJob> {
  return apiClient().get(canopyPath(`/ai/research/${encodeURIComponent(jobId)}`), {
    schema: researchJobSchema,
    ...(signal ? { signal } : {}),
  })
}

export function isSettled(status: ResearchStatus): boolean {
  return status === 'complete' || status === 'failed'
}

export const RESEARCH_POLL_INTERVAL_MS = 20_000

export const RESEARCH_TIMEOUT_MS = 20 * 60 * 1000
